import { useEffect, useRef, useState } from "react";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { openCalendly } from "@/lib/calendly";
import { UserMenu } from "@/components/UserMenu";
import { DesktopNavigation } from "@/components/DesktopNavigation";
import { MobileNavigation } from "@/components/MobileNavigation";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const shellRef = useRef(null);
  const toggleRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const desktop = window.matchMedia("(min-width: 1280px)");
    const onResize = () => { if (desktop.matches) setMobileOpen(false); };
    desktop.addEventListener("change", onResize);
    return () => desktop.removeEventListener("change", onResize);
  }, []);

  useEffect(() => {
    if (!mobileOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    shellRef.current?.querySelector('[data-testid="mobile-nav-group-about"]')?.focus();
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setMobileOpen(false);
        toggleRef.current?.focus();
      }
      if (event.key !== "Tab") return;
      const controls = [...shellRef.current.querySelectorAll('a[href], button:not([disabled]), [tabindex="0"]')]
        .filter((element) => element.tabIndex >= 0 && element.getClientRects().length > 0);
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [mobileOpen]);

  const closeMobile = () => {
    setMobileOpen(false);
    toggleRef.current?.focus();
  };

  return (
    <div ref={shellRef} data-testid="navigation-shell" className="h-[68px] md:h-[72px]">
      <header data-testid="site-header" className={`fixed top-0 inset-x-0 z-50 transition-[background-color,backdrop-filter] duration-300 ${scrolled ? "bg-white/85 backdrop-blur-xl" : "bg-white/30 backdrop-blur-sm"}`}>
        <div aria-hidden="true" className="absolute inset-x-0 top-0 h-px" style={{ background: "linear-gradient(90deg, transparent 0%, rgba(15,17,23,0.18) 20%, rgba(15,17,23,0.28) 50%, rgba(15,17,23,0.18) 80%, transparent 100%)" }} />
        <div className="max-w-[1500px] mx-auto px-5 md:px-8 h-[68px] md:h-[72px] flex items-center gap-4">
          <a href="/" data-testid="logo-link" aria-label="DCYB home" className="flex items-center shrink-0 group">
            <img src="/dcyb-wordmark.png" alt="DCYB" className="h-7 md:h-[30px] w-auto object-contain transition-transform duration-300 group-hover:scale-[1.02]" draggable="false" />
          </a>
          <DesktopNavigation />
          <div className="flex items-center gap-2 md:gap-3 shrink-0 ml-auto xl:ml-2">
            <div className="hidden md:block"><UserMenu /></div>
            <a href="/#book" onClick={(event) => { if (mobileOpen) closeMobile(); openCalendly(event); }} data-testid="header-book-meeting-btn" className="group chrome-btn-dark chrome-btn-dark-glow inline-flex items-center gap-2 font-mono-label text-[10.5px] md:text-[11px] tracking-[0.14em] px-3.5 md:px-4 py-2.5 whitespace-nowrap">
              <span className="live-dot" aria-hidden="true" />
              <span className="hidden 2xl:inline">Book Meeting</span>
              <span className="2xl:hidden">Book</span>
              <ArrowUpRight size={14} aria-hidden="true" className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <button ref={toggleRef} type="button" data-testid="mobile-menu-toggle" aria-label={mobileOpen ? "Close menu" : "Open menu"} aria-expanded={mobileOpen} aria-controls="mobile-menu" onClick={() => setMobileOpen((open) => !open)} className="xl:hidden inline-flex items-center justify-center w-10 h-10 border hairline rounded-md bg-white/70 backdrop-blur text-[#0F1117] hover:bg-white transition-colors">
              {mobileOpen ? <X size={18} aria-hidden="true" /> : <Menu size={18} aria-hidden="true" />}
            </button>
          </div>
        </div>
        <div aria-hidden="true" className={`absolute inset-x-0 bottom-0 h-px transition-opacity duration-300 ${scrolled ? "opacity-100" : "opacity-0"}`} style={{ background: "linear-gradient(90deg, transparent 0%, #C8CBD1 18%, #F6F7F9 38%, #FFFFFF 50%, #F6F7F9 62%, #C8CBD1 82%, transparent 100%)" }} />
        <div aria-hidden="true" className={`absolute inset-x-0 bottom-[-1px] h-px transition-opacity duration-300 ${scrolled ? "opacity-100" : "opacity-0"}`} style={{ background: "rgba(15,17,23,0.08)" }} />
      </header>
      {mobileOpen && <MobileNavigation onClose={closeMobile} />}
    </div>
  );
}